// Financial Freedom Calculator -- state engine + TanStack Query.
//
// This is the core of the feature's frontend architecture. Although the backend
// requires a POST, the calculation is side-effect free and idempotent for the
// user, so we wire it up with `useQuery` (not `useMutation`). That lets us tie
// the query key directly to the slider state for an "auto-calculating" feel.
//
// To avoid spamming the backend while a user drags a slider, we debounce the
// inputs 400ms and key the query off the DEBOUNCED value.

import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { calculateFinancialFreedom } from '../api/queries';
import type { FinancialFreedomInputs } from '../types';
import { WithdrawalPeriodicity } from '../types';

// Delay execution until the user STOPS dragging a slider / typing.
function useDebounce<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => clearTimeout(handler);
    }, [value, delay]);

    return debouncedValue;
}

// Default state so the user isn't met with a blank, intimidating screen.
const DEFAULT_INPUTS: FinancialFreedomInputs = {
    current_age: 35,
    life_expectancy: 95,
    total_savings_today: 100000,
    stable_annual_income: 120000,
    annual_savings_rate: 20.0,
    desired_withdrawal_amount: 5000,
    withdrawal_periodicity: WithdrawalPeriodicity.MONTHLY,
    target_retirement_age: 60, // Optional: passing this answers Question 2.
    expected_inflation_rate: 2.5,
    expected_market_return: 7.0,
    market_volatility: 15.0,
    lump_sums: [],
    other_incomes: [],
};

export function useFinancialFreedomCalculator() {
    // 1. The instant state: UI sliders read/write here with zero lag.
    const [inputs, setInputs] = useState<FinancialFreedomInputs>(DEFAULT_INPUTS);

    // 2. The debounced state: settles 400ms after the user stops interacting.
    const debouncedInputs = useDebounce(inputs, 400);

    // 3. TanStack Query (v5).
    const query = useQuery({
        queryKey: ['financialFreedom', debouncedInputs],
        queryFn: () => calculateFinancialFreedom(debouncedInputs),
        // Prevents the UI/chart from flashing empty while the next calculation
        // is fetching -- a butter-smooth "live" feel.
        placeholderData: keepPreviousData,
        // The math is deterministic; a result never goes stale unless the inputs
        // (and thus the query key) change.
        staleTime: Infinity,
        retry: 1,
    });

    // 4. Ergonomic single-field updater for slider/input onChange handlers.
    const updateInput = <K extends keyof FinancialFreedomInputs>(
        field: K,
        value: FinancialFreedomInputs[K],
    ) => {
        setInputs((prev) => ({ ...prev, [field]: value }));
    };

    return {
        inputs, // Wire UI inputs to this.
        updateInput, // Use this in onChange handlers.
        setInputs, // Raw setter for complex resets (e.g. adding array items).
        data: query.data, // The unified "God Payload" answers.
        isLoading: query.isLoading, // True on the initial load.
        isFetching: query.isFetching, // True while re-calculating in background.
        isError: query.isError,
        error: query.error as Error | null,
    };
}
